import type { UzDateRange, UzDateRangePreset } from '@utazzo/ui';

/**
 * Date-range filter for the Lead inbox. Lives in URL search params
 * (`?range=<preset>&from=<YYYY-MM-DD>&to=<YYYY-MM-DD>`) so both the
 * Pipeline tab and the All-leads tab pick up the same window, and
 * shared links land on the same view.
 *
 * Three legal states:
 *   - preset = 'all'  → no date filter (no from/to params emitted)
 *   - preset = '<name>' → relative window computed at read time (no from/to)
 *   - preset = 'custom' → from + to come from URL params verbatim
 */

export type LeadDateRangePresetKey =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days'
  | 'this_month'
  | 'last_month'
  | 'all'
  | 'custom';

export const DEFAULT_RANGE_PRESET: LeadDateRangePresetKey = 'this_month';

/**
 * Every date-window filter this file emits ends up as a `YYYY-MM-DD` in the
 * URL and the server interprets that string as an IST calendar day
 * (`istDayStartUtc` / `istDayEndUtc` in `leads.service.ts`). Computing the
 * boundaries in the BROWSER's local timezone therefore drifts whenever the
 * browser is not IST - a UTC-locale laptop viewing the app at IST 02:00
 * would ask the server for "today = yesterday IST", pulling in the previous
 * IST day's leads under a "Today's Fresh" tile. We anchor all preset math
 * to IST here so the picker matches the server's day boundaries on any TZ.
 */
const IST_TIME_ZONE = 'Asia/Kolkata';

const IST_YMD_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: IST_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Break a Date into `{year, month, day}` numbers as observed in Asia/Kolkata. */
const istYMD = (d: Date): { year: number; month: number; day: number } => {
  // 'en-CA' formats as `YYYY-MM-DD` in every browser, giving us a stable
  // parse target without a locale-specific separator to chase.
  const [y, m, day] = IST_YMD_FORMATTER.format(d).split('-').map(Number);
  return { year: y!, month: m!, day: day! };
};

/** `YYYY-MM-DD` for the given Date's IST calendar day (default now). */
export const istIsoDate = (d: Date = new Date()): string => {
  const { year, month, day } = istYMD(d);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * A Date whose LOCAL-time y/m/d/h/m/s/ms fields equal the IST wall-clock
 * representation of `d`. `startOfDay`/`endOfDay` (`setHours`) can then mutate
 * these safely - clamping the LOCAL fields to 00:00 or 23:59 has the same
 * effect as clamping the IST wall clock, which is what the server expects.
 * The absolute UTC instant this Date represents is meaningless; only the
 * eventual `YYYY-MM-DD` extracted via `isoDate` is used downstream.
 */
const istLocalMirror = (d: Date): Date => {
  const { year, month, day } = istYMD(d);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

/** Local-mirror of "IST today". Preset ranges work in this mirror. */
const istTodayLocal = (): Date => istLocalMirror(new Date());

const daysAgo = (n: number): Date => {
  const x = istTodayLocal();
  x.setDate(x.getDate() - n);
  return x;
};

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/**
 * The preset list shown in `UzDateRangePicker`. `'all'` returns nulls so the
 * picker label shows "All time" and the URL drops the from/to params.
 */
export const LEAD_RANGE_PRESETS: ReadonlyArray<UzDateRangePreset> = [
  {
    value: 'today',
    label: 'Today',
    range: () => {
      const t = istTodayLocal();
      return { from: startOfDay(t), to: endOfDay(t) };
    },
  },
  {
    value: 'yesterday',
    label: 'Yesterday',
    range: () => {
      const y = daysAgo(1);
      return { from: startOfDay(y), to: endOfDay(y) };
    },
  },
  {
    value: 'last_7_days',
    label: 'Last 7 days',
    range: () => ({ from: startOfDay(daysAgo(6)), to: endOfDay(istTodayLocal()) }),
  },
  {
    value: 'last_30_days',
    label: 'Last 30 days',
    range: () => ({ from: startOfDay(daysAgo(29)), to: endOfDay(istTodayLocal()) }),
  },
  {
    value: 'last_90_days',
    label: 'Last 90 days',
    range: () => ({ from: startOfDay(daysAgo(89)), to: endOfDay(istTodayLocal()) }),
  },
  {
    value: 'this_month',
    label: 'This month',
    range: () => {
      const t = istTodayLocal();
      return { from: startOfMonth(t), to: endOfDay(t) };
    },
  },
  {
    value: 'last_month',
    label: 'Last month',
    range: () => {
      const now = istTodayLocal();
      const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: startOfMonth(lm), to: endOfDay(endOfMonth(lm)) };
    },
  },
  {
    value: 'all',
    label: 'All time',
    range: () => ({ from: null, to: null }),
  },
];

/**
 * Resolve the preset (or custom range) currently in the URL to a concrete
 * `{ from, to }` pair. Returns `null`s when the preset is `'all'`.
 */
export const resolveLeadDateRange = (
  preset: LeadDateRangePresetKey,
  fromIso: string | null,
  toIso: string | null,
): UzDateRange => {
  if (preset === 'custom') {
    return {
      from: fromIso ? new Date(`${fromIso}T00:00:00`) : null,
      to: toIso ? new Date(`${toIso}T23:59:59.999`) : null,
    };
  }
  const found = LEAD_RANGE_PRESETS.find((p) => p.value === preset);
  if (!found) return { from: null, to: null };
  return found.range();
};

/** Format a Date as `YYYY-MM-DD` for the API + URL. */
export const isoDate = (d: Date | null): string | null => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Parse the preset key from a URL param, falling back to the default. */
export const parsePresetKey = (raw: string | null): LeadDateRangePresetKey => {
  const known: LeadDateRangePresetKey[] = [
    'today',
    'yesterday',
    'last_7_days',
    'last_30_days',
    'last_90_days',
    'this_month',
    'last_month',
    'all',
    'custom',
  ];
  return known.find((k) => k === raw) ?? DEFAULT_RANGE_PRESET;
};
